import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useSyncExternalStore } from "react";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { getAccessToken } from "../../api/getAccessToken";
import type { SportsbookSearchType } from "../../components/Promotions/DynamicBonuses/constants";
import { getAdminNow } from "../../utils/dateService";

export type SportsbookDropdownItem = {
  id: string;
  name: string;
};

type TournamentApiItem = {
  tournament_id?: string;
  id?: string;
  title?: string;
  tournament_name?: string;
};

type MatchApiItem = {
  event_id?: string;
  id?: string;
  title?: string;
  event_name?: string;
};

type MarketApiItem = {
  market_id?: string;
  id?: string;
  title?: string;
};

type ApiResponse<T> = {
  success?: boolean;
  data?: T[];
};

const SEARCH_LIMIT = 20;
/**
 * events-dropdown rejects ranges > 4 days (`INVALID_QUERY` /
 * `sportsbook.error.dropdown_date_range_exceeded`). Same window as Early Payout.
 */
const MATCH_DATE_RANGE_DAYS = 4;
/** Fetch a wide page for client-side substring filter; the API trie is prefix-only. */
const CLIENT_SEARCH_FETCH_LIMIT = 1000;

function usesClientSideSearch(type: SportsbookSearchType): boolean {
  return type === "match" || type === "tournament";
}

function getMatchDateRange(): { start_date: string; end_date: string } {
  const today = getAdminNow();
  return {
    start_date: today.format("YYYY-MM-DD"),
    end_date: today.add(MATCH_DATE_RANGE_DAYS, "day").format("YYYY-MM-DD"),
  };
}

function routeForType(type: SportsbookSearchType): string {
  switch (type) {
    case "tournament":
      return API_ROUTES.GET_SPORTSBOOK_PROMO_TOURNAMENTS_DROPDOWN;
    case "match":
      return API_ROUTES.GET_SPORTSBOOK_PROMO_MATCHES_DROPDOWN;
    case "market":
      return API_ROUTES.GET_SPORTSBOOK_PROMO_MARKETS_DROPDOWN;
  }
}

function readId(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return "";
}

function toDropdownItem(
  id: string,
  ...names: unknown[]
): SportsbookDropdownItem | null {
  if (!id) return null;
  const name = names.find(
    (n): n is string => typeof n === "string" && n.trim().length > 0,
  );
  return { id, name: name?.trim() || id };
}

function normalizeItems(
  type: SportsbookSearchType,
  data: unknown[],
): SportsbookDropdownItem[] {
  const mapped = (() => {
    switch (type) {
      case "tournament":
        return (data as TournamentApiItem[]).map((item) =>
          toDropdownItem(
            readId(item.tournament_id, item.id),
            item.title,
            item.tournament_name,
          ),
        );
      case "match":
        return (data as MatchApiItem[]).map((item) =>
          toDropdownItem(
            readId(item.event_id, item.id),
            item.title,
            item.event_name,
          ),
        );
      case "market":
        return (data as MarketApiItem[]).map((item) =>
          toDropdownItem(readId(item.market_id, item.id), item.title),
        );
    }
  })();
  return mapped.filter((item): item is SportsbookDropdownItem => item !== null);
}

function itemMatchesSearch(item: SportsbookDropdownItem, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const name = (item.name ?? "").toLowerCase();
  const id = (item.id ?? "").toLowerCase();
  return name.includes(needle) || id.includes(needle);
}

async function fetchSportsbookDropdown(
  type: SportsbookSearchType,
  search: string,
  tournamentId?: string,
): Promise<SportsbookDropdownItem[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const isMatch = type === "match";
  const clientFilter = usesClientSideSearch(type);
  const params: Record<string, string> = {
    search_limit: String(clientFilter ? CLIENT_SEARCH_FETCH_LIMIT : SEARCH_LIMIT),
  };
  // leagues-dropdown rejects empty search_term (min_length=1). The leagues and
  // events tries are prefix-only (`USA` misses "MLB - USA", `Yankees` misses
  // "New York Yankees"), so those types fetch the full list and filter here.
  if (search && !clientFilter) {
    params.search_term = search;
  }
  if (isMatch) {
    params.event_type = "match";
    const { start_date, end_date } = getMatchDateRange();
    params.start_date = start_date;
    params.end_date = end_date;
    if (tournamentId) {
      params.tournament_id = tournamentId;
    }
  }
  const response = await instance.get(routeForType(type), { params });
  const body = response?.data as ApiResponse<unknown> | undefined;
  const rows = Array.isArray(body?.data) ? body.data : [];
  const items = normalizeItems(type, rows);
  const visible = clientFilter ? items : items.slice(0, SEARCH_LIMIT);
  return visible.sort((a, b) =>
    (a.name || a.id).localeCompare(b.name || b.id),
  );
}

export const SPORTSBOOK_RESTRICTION_DROPDOWN_QUERY_KEY =
  "SPORTSBOOK_RESTRICTION_DROPDOWN" as const;

export function useSportsbookRestrictionDropdown(
  type: SportsbookSearchType,
  search: string,
  enabled = true,
  tournamentId?: string,
) {
  const clientFilter = usesClientSideSearch(type);
  const query = useQuery<SportsbookDropdownItem[], Error>({
    queryKey: [
      SPORTSBOOK_RESTRICTION_DROPDOWN_QUERY_KEY,
      type,
      clientFilter ? "" : search,
      tournamentId,
    ],
    queryFn: () =>
      fetchSportsbookDropdown(type, clientFilter ? "" : search, tournamentId),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const data = useMemo(() => {
    const items = query.data ?? [];
    const filtered = clientFilter
      ? items.filter((item) => itemMatchesSearch(item, search))
      : items;
    return filtered.slice(0, SEARCH_LIMIT);
  }, [query.data, clientFilter, search]);

  return { ...query, data };
}

const titleFromCachedItems = (
  id: string,
  data: SportsbookDropdownItem[] | undefined,
): string | undefined => {
  const match = data?.find((item) => item.id === id);
  const title = match?.name?.trim();
  if (!title || title === id) return undefined;
  return title;
};

const readCachedRestrictionName = (
  queryClient: ReturnType<typeof useQueryClient>,
  type: SportsbookSearchType | undefined,
  id: string | undefined,
): string | undefined => {
  if (!type || !id) return undefined;
  const queries = queryClient.getQueriesData<SportsbookDropdownItem[]>({
    queryKey: [SPORTSBOOK_RESTRICTION_DROPDOWN_QUERY_KEY, type],
  });
  for (const [, data] of queries) {
    const title = titleFromCachedItems(id, data);
    if (title) return title;
  }
  return undefined;
};

/**
 * Reads a restriction title from any cached sportsbook dropdown search.
 * Destination cannot look up `sr:tournament:298` by ID (that search returns
 * the ID); the picker already cached the title from a name search.
 */
export function useSportsbookRestrictionNameFromCache(
  type: SportsbookSearchType | undefined,
  id: string | undefined,
): string | undefined {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache();
  return useSyncExternalStore(
    (onStoreChange) =>
      cache.subscribe((event) => {
        if (
          event?.query?.queryKey?.[0] === SPORTSBOOK_RESTRICTION_DROPDOWN_QUERY_KEY
        ) {
          onStoreChange();
        }
      }),
    () => readCachedRestrictionName(queryClient, type, id),
    () => undefined,
  );
}
